import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { Reveal, MaskedLines, FadeIn } from "../components/motion/Reveal";
import EditorialMarquee from "../components/EditorialMarquee";
import ManufacturingCarousel from "../components/ManufacturingCarousel";
import PageMeta from "../components/PageMeta";
import { useSettings } from "../context/SettingsContext";
import { IMAGES, pimg } from "../lib/site";

const WHY_US = [
  ["Experience", "12+ years manufacturing for global sports brands."],
  ["Quality", "Batch-level QC with documented inspection reports."],
  ["Reliability", "98% on-time shipment record across 40+ countries."],
  ["Customization", "From Pantone-matched colors to full retail packaging."],
  ["Export Capability", "FOB, CIF, DDP — documentation handled end to end."],
];

const PROCESS = [
  ["Inquiry", "Share your product, quantity, and requirements."],
  ["Product Selection", "We recommend constructions and materials."],
  ["Sample", "Physical sample produced in 7–10 days."],
  ["Approval", "You sign off on every detail before production."],
  ["Production", "Mass production with in-line quality checks."],
  ["Shipment", "Packed, documented, and shipped to your port."],
];

const About = () => {
  const site = useSettings();
  const about = site.aboutPage || {};

  const heroSubtitle = about.hero_subtitle || "About Us — Pickleballgearz";
  const heroTitle = about.hero_title || "THE FACTORY BEHIND WINNING BRANDS";
  const heroDesc = about.hero_description || "Pickleballgearz is a dedicated pickleball production house. What started as a two-machine workshop now runs thermoforming, cold-press, and rotomolding lines serving brands across 40+ countries.";
  const missionTitle = about.mission_title || "Our Mission";
  const missionStatement = about.mission_statement || "To make world-class pickleball manufacturing accessible to brands of every size with uncompromising quality.";
  const visionTitle = about.vision_title || "Our Vision";
  const visionStatement = about.vision_statement || "To be the premier global OEM/ODM choice for high-performance paddles, tournament balls, and court equipment.";

  return (
    <div data-testid="about-page">
      <PageMeta
        title="About Us — Pickleballgearz"
        description={heroDesc}
      />
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-20 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <p className="mono-caption mb-8 text-neutral-500">{heroSubtitle}</p>
          </FadeIn>
          <h1 className="max-w-4xl font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={[heroTitle.toUpperCase(), <>WINNING BRANDS<span className="text-brand-red">.</span></>]} />
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
        <SectionHeading number="01" title="Who We Are" />
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <img
              src="/images/about/pickleball-factory.jpeg"
              alt="Pickleballgearz production facility"
              className="aspect-[4/3] w-full border border-black/10 object-cover shadow-sm"
            />
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-lg leading-relaxed text-neutral-600">
              {heroDesc}
            </p>
            <div className="mt-8 space-y-4 border-l-2 border-brand-red pl-5">
              <div>
                <h4 className="font-display text-lg font-medium text-brand-ink">{missionTitle}</h4>
                <p className="mt-1 text-sm leading-relaxed text-neutral-500">{missionStatement}</p>
              </div>
              <div>
                <h4 className="font-display text-lg font-medium text-brand-ink">{visionTitle}</h4>
                <p className="mt-1 text-sm leading-relaxed text-neutral-500">{visionStatement}</p>
              </div>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-black/10 pt-8">
              {[["12+", "Years"], ["40+", "Countries"], ["1M+", "Units / Year"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display text-3xl font-semibold">{n}</p>
                  <p className="mono-caption mt-1 text-neutral-400">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

    <section className="bg-brand-ink py-24 text-white md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <SectionHeading dark number="02" title="Manufacturing" sub="A facility built around one sport — nothing else." />
        <div className="grid gap-px border border-white/15 bg-white/15 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Production Facility", "12,000 m² plant with dedicated paddle, ball, and soft-goods lines."],
            ["Manufacturing Process", "Thermoforming, cold-press lamination, and rotational molding under one roof."],
            ["Quality Control", "Raw material checks, in-line inspection, and pre-shipment AQL sampling."],
            ["Technology", "CNC tooling, UV print lines, and automated drilling for precision consistency."],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <div className="h-full bg-brand-ink p-8">
                <p className="font-mono text-xs text-brand-red">0{i + 1}</p>
                <h3 className="mt-4 font-display text-xl font-medium tracking-tight">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/50">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12">
          <ManufacturingCarousel />
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
      <SectionHeading number="03" title="Why Us" />
      <div className="divide-y divide-black/10 border-y border-black/10">
        {WHY_US.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.05}>
            <div className="group grid items-baseline gap-2 py-6 sm:grid-cols-12" data-testid={`whyus-${i}`}>
              <span className="font-mono text-sm text-brand-red sm:col-span-1">0{i + 1}</span>
              <h3 className="font-display text-2xl font-medium tracking-tight transition-transform group-hover:translate-x-2 sm:col-span-4">{t}</h3>
              <p className="text-neutral-500 sm:col-span-7">
                {t === "Customization" ? (
                  <>
                    From{" "}
                    <a
                      href="/PANTONE.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-brand-red underline decoration-brand-red/40 underline-offset-4 hover:decoration-brand-red hover:text-brand-reddark transition-colors"
                      data-testid="whyus-pantone-link"
                    >
                      Pantone-matched colors <ArrowUpRight size={14} />
                    </a>{" "}
                    to full retail packaging.
                  </>
                ) : (
                  d
                )}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <EditorialMarquee />

    <section className="mx-auto max-w-[1400px] px-5 py-24 md:py-32 lg:px-10">
      <SectionHeading number="04" title="Our Process" sub="Six steps from first message to final container." />
      <div className="grid gap-px border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-3">
        {PROCESS.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.05}>
            <div className="h-full bg-white p-8">
              <p className="font-mono text-2xl text-brand-red">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-5 font-display text-xl font-medium tracking-tight">{t}</h3>
              <p className="mt-2 text-sm text-neutral-500">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-t border-black/10 bg-brand-smoke">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-8 px-5 py-20 lg:px-10">
        <Reveal>
          <h2 className="max-w-xl font-display text-3xl font-medium tracking-tight md:text-4xl">
            Ready to build your pickleball line<span className="text-brand-red">?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/inquiry" className="btn-primary" data-testid="about-cta-inquiry">
            Start Your Inquiry <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  </div>
  );
};

export default About;
